import SelectableTile from '@carvana/ds-selectable-tile';
import omit from 'lodash.omit';
import { useContext, useState } from 'react';
import {
  CardImage,
  HeadlineRow,
  ImageContainer,
  ImageUnavailable,
  PriceContainer,
  PriceLabel,
  PriceValue,
  StockCount,
  Title
} from '@/new-cars/common/ConfiguratorCard.styles';
import tileStyles from '@/new-cars/common/SelectableTile.module.css';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type {
  SelectedTruckBodyStyle,
  TruckBodyStyleResult
} from '~/contracts/new-cars';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { isFallbackImage } from '~/utilities/isFallbackImage';
import optimizeCardImage from '~/utilities/new-cars/optimizeCardImage';
import { Content } from './TruckBodyStyleCard.styles';

type OwnProps = {
  disabled?: boolean;
  selected: boolean;
  truckBodyStyle: TruckBodyStyleResult;
};

export default function TruckBodyStyleCard({
  disabled = false,
  selected,
  truckBodyStyle
}: OwnProps) {
  const { setSelectedTruckBodyStyle } = useContext(ParentModelContext);
  const [isImageBroken, setIsImageBroken] = useState(false);

  const { count, imageUrl, lowestPrice, title } = truckBodyStyle;
  const isFallback = isFallbackImage(imageUrl);
  const optimizedImageUrl = optimizeCardImage(imageUrl, isFallback);

  return (
    <SelectableTile
      className={tileStyles.tile}
      disabled={disabled}
      selected={selected}
      onClick={() => {
        setSelectedTruckBodyStyle(
          omit(
            { ...truckBodyStyle, ...truckBodyStyle.next },
            'next'
          ) as SelectedTruckBodyStyle
        );
      }}
      data-analytics-attributes={JSON.stringify({
        level: 'Body Style',
        section: 'Body Style',
        text: title,
        tileAction: selected ? 'selected' : 'deselected'
      })}
      data-analytics-id="New Cars"
      data-testid="truck-body-style-card"
      value={title}
      variant="simple-select"
    >
      <ImageContainer disabled={disabled} isFallback={isFallback}>
        {optimizedImageUrl && !isImageBroken ? (
          <CardImage
            alt={title}
            data-testid="truck-body-style-card-image"
            loading="lazy"
            onError={() => setIsImageBroken(true)}
            src={optimizedImageUrl}
          />
        ) : (
          <ImageUnavailable>Image unavailable</ImageUnavailable>
        )}
      </ImageContainer>
      <Content>
        <HeadlineRow>
          <Title disabled={disabled}>{title}</Title>
          <PriceContainer data-testid="truck-body-style-card-price">
            <PriceLabel>from </PriceLabel>
            <PriceValue disabled={disabled}>
              ${formatAndDropDecimal(lowestPrice)}
            </PriceValue>
          </PriceContainer>
        </HeadlineRow>
        <StockCount data-testid="truck-body-style-card-stock-count">
          {count} in stock
        </StockCount>
      </Content>
    </SelectableTile>
  );
}
